import { MenuItem, Box, Alert, Typography, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { FC, useState, useMemo } from 'react';
import { Loader, SMBSelect } from '../../components';
import { sendSMSText, sendAuthyText } from '../../fetch';
import { IAuthUserMFAOptions } from '../../models';
import { MFATextOption } from './mfa-text-option';
import { userLogout } from '../../helpers';

interface IMFAMultipleExistingOptionsProps {
  isLoadingMFA: boolean;
  existingMfaOptions: IAuthUserMFAOptions[];
  handleLogin: (e: any, option: IAuthUserMFAOptions, code: string) => void;
  handleSelectedType: (val: IAuthUserMFAOptions) => void;
  setSelectedInputCode: (val: string) => void;
  resetLogin: () => void;
}

export const MFAMultipleExistingOptions: FC<IMFAMultipleExistingOptionsProps> = ({
  isLoadingMFA,
  existingMfaOptions,
  handleLogin,
  handleSelectedType,
  setSelectedInputCode,
  resetLogin
}) => {
  const { enqueueSnackbar } = useSnackbar();
  // state
  const [selectedMfaType, setSelectedMfaType] = useState<string>('');
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [isSendingText, setIsSendingText] = useState<boolean>(false);
  const selectedOption = useMemo(
    () =>
      existingMfaOptions.find(
        option => option.twoFactorAuthenticationId === Number(selectedMfaType)
      ),
    [selectedMfaType, existingMfaOptions]
  );

  return (
    <Box mt={1}>
      <Box mb={1}>
        <Alert severity="info">
          {selectedOption?.type === 'App' || selectedOption?.type === 'Authy'
            ? `Enter the code in your authenticator ${
                selectedOption?.type === 'Authy' ? 'Authy' : ''
              } app.`
            : 'Select an multifactor authentication option below.'}
        </Alert>
      </Box>
      <SMBSelect
        label="Type"
        size="small"
        value={selectedMfaType}
        onChange={async ({ target: { value } }) => {
          setSelectedMfaType(value);
          const selected = existingMfaOptions.find(
            option => option.twoFactorAuthenticationId === Number(value)
          );
          if (selected) {
            handleSelectedType(selected);
          }
          if (selected?.type === 'Text' || selected?.type === 'Authy') {
            setIsSendingText(true);
            try {
              selected?.type === 'Authy'
                ? await sendAuthyText(selected.twoFactorAuthenticationId)
                : await sendSMSText(selected.twoFactorAuthenticationId);
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Error sending, please try again.', {
                variant: 'error'
              });
            } finally {
              setIsSendingText(false);
            }
          }
        }}
      >
        {existingMfaOptions.map((type, index) => {
          return (
            <MenuItem
              key={`${index}`}
              value={type.twoFactorAuthenticationId}
              sx={{ display: 'block' }}
            >
              <span>{type.typeDisplayName}</span>
              <Typography component="span" sx={{ display: 'block' }} variant="caption">
                {type.phoneNumber ? `${type.phoneNumber}` : ''}
                {type.displayName ? `${type.displayName}` : ''}
              </Typography>
            </MenuItem>
          );
        })}
      </SMBSelect>
      {isSendingText && (
        <Box mt={1} mb={0.5}>
          <Loader position="centered" title="Sending..." />
        </Box>
      )}
      {selectedMfaType && (
        <form onSubmit={e => handleLogin(e, selectedOption, selectedCode)}>
          <MFATextOption
            buttonText="Login"
            isButtonDisabled={!selectedCode || isLoadingMFA || isSendingText}
            isLoading={isLoadingMFA}
            textFieldLabel="Code"
            textFieldValue={selectedCode}
            isTextOption={selectedOption?.type === 'Text'}
            authId={selectedOption?.twoFactorAuthenticationId}
            handleChange={(val: string) => {
              setSelectedCode(val);
              setSelectedInputCode(val);
            }}
            handleButtonClick={e => handleLogin(e, selectedOption, selectedCode)}
          />
        </form>
      )}
      <Box mt={1}>
        <Button
          color="primary"
          fullWidth={true}
          variant="text"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={() => {
            userLogout();
            resetLogin();
          }}
        >
          Back to Login
        </Button>
      </Box>
    </Box>
  );
};
